import React, { useState, useEffect } from 'react'
import { Button } from 'antd'
import { DownOutlined, RightOutlined } from '@ant-design/icons'
import type { CustomHeaderProps } from 'ag-grid-react'
import type { IRowNode } from 'ag-grid-enterprise'
import { useAgGridLocale } from '../../../../hooks/useAgGridLocale'

interface ExpandAllHeaderRendererProps<TData = any> extends CustomHeaderProps<TData> {
  /**
   * Optional function to determine if a node should be expanded/collapsed.
   * If not provided, all expandable nodes will be toggled.
   */
  shouldExpand?: (node: IRowNode<TData>) => boolean
}

/**
 * Generic header renderer with expand/collapse all functionality.
 * Can be used with any ag-Grid tree data structure.
 *
 * @example
 * // Use with default behavior (expand all expandable nodes)
 * {
 *   headerComponent: ExpandAllHeaderRenderer
 * }
 *
 * @example
 * // Use with custom filter (e.g., only expand heading rows)
 * {
 *   headerComponent: ExpandAllHeaderRenderer,
 *   headerComponentParams: {
 *     shouldExpand: (node) => node.data?.rowType === 'HEADING'
 *   }
 * }
 */
export const ExpandAllHeaderRenderer = <TData,>({
  api,
  displayName,
  shouldExpand
}: ExpandAllHeaderRendererProps<TData>) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const localeText = useAgGridLocale()

  // Check if any matching nodes are expanded
  useEffect(() => {
    const checkExpandedState = () => {
      let hasExpanded = false
      api.forEachNode(node => {
        const shouldCheck = shouldExpand ? shouldExpand(node) : node.group || (node.allChildrenCount ?? 0) > 0
        if (shouldCheck && node.expanded) {
          hasExpanded = true
        }
      })
      setIsExpanded(hasExpanded)
    }

    // Check initial state
    checkExpandedState()

    // Listen to expansion changes
    const listener = () => checkExpandedState()
    api.addEventListener('rowGroupOpened', listener)

    return () => {
      api.removeEventListener('rowGroupOpened', listener)
    }
  }, [api, shouldExpand])

  const handleToggleAll = () => {
    const expandState = !isExpanded
    api.forEachNode(node => {
      const shouldToggle = shouldExpand ? shouldExpand(node) : node.group || (node.allChildrenCount ?? 0) > 0
      if (shouldToggle) {
        api.setRowNodeExpanded(node, expandState)
      }
    })
    setIsExpanded(expandState)
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        height: '100%'
      }}
    >
      <Button
        type="text"
        size="small"
        icon={isExpanded ? <DownOutlined /> : <RightOutlined />}
        onClick={handleToggleAll}
        aria-label={isExpanded ? localeText.collapseAll : localeText.expandAll}
        style={{ marginLeft: -4 }}
      />
      <span>{displayName}</span>
    </div>
  )
}
